import React, { useState, useEffect } from 'react';
import bcrypt from 'bcryptjs';
import EditUserModal from '../components/Modals/EditUserModal';
import { Search, Plus, Edit, Eye, EyeOff, Check, X } from 'lucide-react';
import { useSocket } from '../context/SocketContext';

const Page = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [org, setOrg] = useState('');
  const [cel, setCel] = useState('');
  const [ident, setIdent] = useState('');
  const [rol, setRol] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserOrg, setCurrentUserOrg] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [message, setMessage] = useState('');
  const { isConnected, socket } = useSocket();

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          process.env.REACT_APP_URL_BASE_BACKEND + '/api/user/profile',
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const userData = await response.json();
        setIsAdmin(userData.rol === 'Admin');
        setCurrentUserOrg(userData.org);
      } catch (error) {
        console.error('Error al verificar rol de usuario', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkUserRole();
    fetchUsers();
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(
        user =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.org.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.ident &&
            user.ident
              .toString()
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 3000); // 3000 milisegundos = 3 segundos

      return () => clearTimeout(timer); // Limpieza si el componente se desmonta
    }
  }, [message]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + '/users',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener usuarios');
      }

      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
      setFilteredUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + '/organizations',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      setOrganizations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching organizations', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = 'El nombre es requerido';
    } else if (name.length < 3) {
      newErrors.name = 'El nombre debe tener al menos 3 caracteres';
    }

    if (!email.trim()) {
      newErrors.email = 'El correo es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Ingrese un correo válido';
    }

    if (!password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!org) {
      newErrors.org = 'La organización es requerida';
    }

    if (!cel.trim()) {
      newErrors.cel = 'El celular es requerido';
    } else if (!/^[0-9+\-\s()]{10,15}$/.test(cel)) {
      newErrors.cel = 'Ingrese un número de celular válido';
    }

    if (!ident.trim()) {
      newErrors.ident = 'La identificación es requerida';
    }

    if (!rol) {
      newErrors.rol = 'El rol es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const hashedPassword = await bcrypt.hash(password, 10);

      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + '/users',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name,
            email,
            password: hashedPassword,
            org,
            cel,
            ident,
            rol,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al agregar usuario');
      }

      fetchUsers();
      resetForm();
      document.getElementById('my_modal_1').close();
      // Mostrar la alerta de éxito
      setShowSuccessAlert(true);
      // Ocultar la alerta después de 3 segundos
      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 3000);
    } catch (error) {
      alert(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPassword('');
    setOrg('');
    setCel('');
    setIdent('');
    setRol('');
    setShowPassword(false);
    setErrors({});
  };

  const handleToggleUser = async (userId, isActive) => {
    try {
      setMessage(''); // Limpiar mensajes anteriores
      
      // Actualizar UI inmediatamente para mejor experiencia (optimista)
      setUsers(users.map(user => 
        user._id === userId ? { ...user, active: isActive } : user
      ));
      
      // Enviar solicitud al servidor
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_URL_BASE_BACKEND}/users/${userId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ 
            active: isActive, 
            closeSessions: true // Solicitar cierre de sesiones si se está deshabilitando
          }),
        }
      );
  
      if (!response.ok) {
        // Revertir cambio optimista si hay error
        setUsers(users.map(user => 
          user._id === userId ? { ...user, active: !isActive } : user
        ));
        throw new Error('Error al actualizar el estado del usuario');
      }
  
      const data = await response.json();
      
      // Mostrar mensaje de éxito
      setMessage(
        isActive 
          ? `Usuario ${data.user.name || userId} habilitado exitosamente` 
          : `Usuario ${data.user.name || userId} inhabilitado exitosamente`
      );
      
      // Verificar estado del socket para diagnóstico
      if (!isConnected && !isActive) {
        console.warn("Socket no conectado. El backend deberá manejar el cierre de sesión.");
        setMessage(prev => `${prev}. Advertencia: Socket desconectado, el cierre de sesión remoto puede no funcionar.`);
      }
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      setMessage(`Error: ${error.message || 'No se pudo procesar la solicitud'}`);
    }
  };

  const handleSaveUser = async editedUser => {
    try {
      const token = localStorage.getItem('token');
      if (editedUser.password) {
        editedUser.password = await bcrypt.hash(editedUser.password, 10);
      }
      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + `/users/${editedUser._id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editedUser),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.error || 'Error al actualizar el usuario');
        return;
      }

      fetchUsers();
      document.getElementById('edit_modal').close();
    } catch (error) {
      console.error('Error al actualizar el usuario', error);
    }
  };

  const renderUserTable = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center p-10">
          <span className="loading loading-spinner loading-lg text-primary"></span>
        </div>
      );
    }

    return (
      <div className="bg-base-100 rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          {message && (
            <div className="alert alert-info mb-4">
              {message}
            </div>
          )}
          <table className="table w-full">
            <thead className="bg-base-200">
              <tr>
                <th className="py-4">Nombre</th>
                <th>Correo</th>
                <th>Organización</th>
                <th>Celular</th>
                <th>Identificación</th>
                <th>Rol</th>
                {isAdmin && (
                  <>
                    <th>Estado</th>
                    <th className="text-center">Acciones</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <tr
                    key={user._id}
                    className="hover:bg-base-200 border-b border-base-200"
                  >
                    <td className="font-medium">{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.org}</td>
                    <td>{user.cel}</td>
                    <td>{user.ident}</td>
                    <td>
                      <span
                        className={`badge ${user.rol === 'Admin' ? 'badge-accent' : 'badge-info'
                          } badge-outline px-3 py-2`}
                      >
                        {user.rol}
                      </span>
                    </td>
                    {isAdmin && (
                      <>
                        <td>
                          <button
                            className={`btn btn-sm ${user.active ? 'btn-success' : 'btn-error'
                              } btn-outline rounded-full`}
                            onClick={() =>
                              handleToggleUser(user._id, !user.active)
                            }
                          >
                            {user.active ? (
                              <>
                                <Check size={16} /> Habilitado
                              </>
                            ) : (
                              <>
                                <X size={16} /> Inhabilitado
                              </>
                            )}
                          </button>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-primary btn-outline rounded-full"
                            onClick={() => {
                              setCurrentUser(user);
                              document.getElementById('edit_modal').showModal();
                            }}
                          >
                            <Edit size={16} /> Editar
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={isAdmin ? 8 : 6}
                    className="text-center py-10 text-base-content/70"
                  >
                    No hay usuarios para mostrar
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {showSuccessAlert && (
        <div role="alert" className="alert alert-success mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>El Usuario se ha creado correctamente</span>
        </div>
      )}
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <h2 className="text-2xl font-bold text-base-content">
            {isAdmin ? 'Gestión de Usuarios' : `Usuarios de ${currentUserOrg}`}
          </h2>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-64">
              <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                <Search size={18} className="text-base-content/60" />
              </div>
              <input
                type="text"
                className="input input-bordered pl-10 w-full"
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>

            {isAdmin && (
              <button
                className="btn btn-primary btn-outline rounded-full"
                onClick={() =>
                  document.getElementById('my_modal_1').showModal()
                }
              >
                <Plus size={18} /> Agregar Usuario
              </button>
            )}
          </div>
        </div>

        <div className="stats shadow mb-6 w-full">
          <div className="stat">
            <div className="stat-title">Total Usuarios</div>
            <div className="stat-value">{users.length}</div>
          </div>
          <div className="stat">
            <div className="stat-title">Usuarios Habilitados</div>
            <div className="stat-value text-success">
              {users.filter(user => user.active).length}
            </div>
          </div>
          <div className="stat">
            <div className="stat-title">Usuarios Inhabilitados</div>
            <div className="stat-value text-error">
              {users.filter(user => !user.active).length}
            </div>
          </div>
        </div>

        {renderUserTable()}
      </div>

      {/* Modal para crear usuario */}
      {isAdmin && (
        <dialog id="my_modal_1" className="modal">
          <div className="modal-box max-w-2xl">
            <h3 className="font-bold text-2xl mb-4 text-center text-primary">
              Nuevo Usuario
            </h3>
            <div className="divider"></div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      Nombre completo
                    </span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered w-full ${errors.name ? 'input-error' : ''
                      }`}
                    value={name}
                    onChange={e => {
                      setName(e.target.value);
                      if (errors.name) {
                        setErrors({ ...errors, name: '' });
                      }
                    }}
                    placeholder="Nombre del usuario"
                  />
                  {errors.name && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.name}
                      </span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      Correo electrónico
                    </span>
                  </label>
                  <input
                    type="email"
                    className={`input input-bordered w-full ${errors.email ? 'input-error' : ''
                      }`}
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      if (errors.email) {
                        setErrors({ ...errors, email: '' });
                      }
                    }}
                    placeholder="correo@ejemplo.com"
                  />
                  {errors.email && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.email}
                      </span>
                    </label>
                  )}
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Contraseña</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`input input-bordered w-full pr-12 ${errors.password ? 'input-error' : ''
                      }`}
                    value={password}
                    onChange={e => {
                      setPassword(e.target.value);
                      if (errors.password) {
                        setErrors({ ...errors, password: '' });
                      }
                    }}
                    placeholder="Contraseña"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 px-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.password}
                    </span>
                  </label>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Celular</span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered w-full ${errors.cel ? 'input-error' : ''
                      }`}
                    value={cel}
                    onChange={e => {
                      setCel(e.target.value);
                      if (errors.cel) {
                        setErrors({ ...errors, cel: '' });
                      }
                    }}
                    placeholder="+57 (000) 000-0000"
                  />
                  {errors.cel && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.cel}
                      </span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      Identificación
                    </span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered w-full ${errors.ident ? 'input-error' : ''
                      }`}
                    value={ident}
                    onChange={e => {
                      setIdent(e.target.value);
                      if (errors.ident) {
                        setErrors({ ...errors, ident: '' });
                      }
                    }}
                    placeholder="Número de ID"
                  />
                  {errors.ident && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.ident}
                      </span>
                    </label>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      Organización
                    </span>
                  </label>
                  <select
                    className={`select select-bordered w-full ${errors.org ? 'select-error' : ''
                      }`}
                    value={org}
                    onChange={e => {
                      setOrg(e.target.value);
                      if (errors.org) {
                        setErrors({ ...errors, org: '' });
                      }
                    }}
                  >
                    <option value="" disabled>
                      Seleccione una organización
                    </option>
                    {organizations.map(org => (
                      <option key={org._id} value={org.name}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  {errors.org && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.org}
                      </span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">Rol</span>
                  </label>
                  <select
                    className={`select select-bordered w-full ${errors.rol ? 'select-error' : ''
                      }`}
                    value={rol}
                    onChange={e => {
                      setRol(e.target.value);
                      if (errors.rol) {
                        setErrors({ ...errors, rol: '' });
                      }
                    }}
                  >
                    <option value="" disabled>
                      Seleccione un rol
                    </option>
                    <option value="Admin">Administrador</option>
                    <option value="User">Usuario</option>
                  </select>
                  {errors.rol && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.rol}
                      </span>
                    </label>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    resetForm();
                    document.getElementById('my_modal_1').close();
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="loading loading-spinner"></span>
                  ) : (
                    'Crear Usuario'
                  )}
                </button>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={resetForm}>cerrar</button>
          </form>
        </dialog>
      )}

      <EditUserModal
        user={currentUser}
        onSave={handleSaveUser}
        onClose={() => document.getElementById('edit_modal').close()}
        organizations={organizations}
        isAdmin={isAdmin}
        allowPasswordChange={true}
      />
    </div>
  );
};

export default Page;